/**
 * PURPOSE: Configurar la información pública de una disciplina desde Winscore.
 * RESPONSIBILITIES: Editar fechas, sedes filtradas por evento, reunión técnica, documentos y entrenamientos que consume Hayllis 2.0.
 * COLLABORATORS: hayllisV2SportInformationService.js, ConfigCategoryArea.jsx y backend/src/hayllis-v2/sport-information.
 * ROUTE: src/components/Cards/CardConfiguration/CardConfigSportInformation/CardConfigSportInformation.jsx
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FaBookOpen, FaPlus, FaSave, FaTrash } from "react-icons/fa";

import {
  getSportInformation,
  listParticipantInstitutionOptions,
  listVenueOptions,
  updateSportInformation,
} from "../../../../services/hayllisV2SportInformationService.js";
import { SearchableVenueSelect } from "./SearchableVenueSelect.jsx";
import { TrainingEditor } from "./TrainingEditor.jsx";
import {
  createTraining,
  hydrateTraining,
  orderTrainingsForSave,
} from "./trainingEditor.utils.js";

const createDocument = () => ({ title: "", link: "" });
const createEmptyForm = () => ({
  competition: { startDate: "", endDate: "" },
  venueIds: [],
  technicalMeeting: { date: "", time: "", venueId: "", locationText: "" },
  documents: [createDocument()],
  trainings: [createTraining()],
});

const normalizeVenueId = (venue) => venue?.idsede || venue?.id || "";
const normalizeVenueName = (venue) => venue?.name || venue?.nombre || `Sede ${normalizeVenueId(venue)}`;
const normalizeInstitutionId = (institution) => institution?.idinstitution || institution?.id || "";
const normalizeInstitutionName = (institution) => institution?.name || institution?.nombre || `Institución ${normalizeInstitutionId(institution)}`;

const CardConfigSportInformation = () => {
  const { idevent, idsport } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadWarning, setLoadWarning] = useState("");
  const [venuesCatalog, setVenuesCatalog] = useState([]);
  const [institutionsCatalog, setInstitutionsCatalog] = useState([]);
  const [selectedCompetitionVenueId, setSelectedCompetitionVenueId] = useState("");
  const [form, setForm] = useState(createEmptyForm);

  const venueOptions = useMemo(() => {
    return venuesCatalog
      .map((venue) => ({
        id: String(normalizeVenueId(venue)),
        name: normalizeVenueName(venue),
        address: venue?.address || "",
      }))
      .filter((venue) => venue.id);
  }, [venuesCatalog]);

  const selectedCompetitionVenues = useMemo(() => {
    return form.venueIds
      .map((venueId) => venueOptions.find((venue) => venue.id === String(venueId)))
      .filter(Boolean);
  }, [form.venueIds, venueOptions]);

  const availableCompetitionVenues = useMemo(() => {
    return venueOptions.filter((venue) => !form.venueIds.includes(venue.id));
  }, [form.venueIds, venueOptions]);

  const institutionOptions = useMemo(() => {
    return institutionsCatalog
      .map((institution) => ({
        id: String(normalizeInstitutionId(institution)),
        name: normalizeInstitutionName(institution),
      }))
      .filter((institution) => institution.id);
  }, [institutionsCatalog]);

  const loadData = useCallback(async () => {
    if (!idevent || !idsport) return;

    setLoading(true);
    setLoadWarning("");
    try {
      const [informationPayload, venues, institutions] = await Promise.all([
        getSportInformation({ idevent, idsport }),
        listVenueOptions({ idevent }),
        listParticipantInstitutionOptions({ idevent, idsport }),
      ]);

      const information = informationPayload?.information || null;
      const loadedInstitutionOptions = institutions
        .map((institution) => ({
          id: String(normalizeInstitutionId(institution)),
          name: normalizeInstitutionName(institution),
        }))
        .filter((institution) => institution.id);

      setVenuesCatalog(venues);
      setInstitutionsCatalog(institutions);

      if (information) {
        setForm({
          competition: {
            startDate: information.competition?.startDate || "",
            endDate: information.competition?.endDate || "",
          },
          venueIds: Array.isArray(information.venues)
            ? information.venues.map((venue) => String(venue.id)).filter(Boolean)
            : [],
          technicalMeeting: {
            date: information.technicalMeeting?.date || "",
            time: information.technicalMeeting?.time || "",
            venueId: information.technicalMeeting?.venue?.id ? String(information.technicalMeeting.venue.id) : "",
            locationText: information.technicalMeeting?.locationText || "",
          },
          documents: information.documents?.length
            ? information.documents.map((document) => ({ title: document.title || "", link: document.link || "" }))
            : [createDocument()],
          trainings: information.trainings?.length
            ? information.trainings.map((training, index) =>
                hydrateTraining(training, index, loadedInstitutionOptions),
              )
            : [createTraining()],
        });
      } else {
        setForm(createEmptyForm());
      }
    } catch (error) {
      setForm(createEmptyForm());
      setLoadWarning(
        error?.response?.data?.msg ||
          "No se pudo cargar la información pública. Puedes reintentar sin que se bloquee el resto de la configuración."
      );
      console.error("Error loading sport information", error);
    } finally {
      setLoading(false);
    }
  }, [idevent, idsport]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateCompetition = (key, value) => {
    setForm((current) => ({
      ...current,
      competition: { ...current.competition, [key]: value },
    }));
  };

  const updateTechnicalMeeting = (key, value) => {
    setForm((current) => ({
      ...current,
      technicalMeeting: { ...current.technicalMeeting, [key]: value },
    }));
  };

  const addCompetitionVenue = () => {
    if (!selectedCompetitionVenueId) return;

    setForm((current) => {
      if (current.venueIds.includes(selectedCompetitionVenueId)) return current;

      return {
        ...current,
        venueIds: [...current.venueIds, selectedCompetitionVenueId],
      };
    });

    setSelectedCompetitionVenueId("");
  };

  const removeCompetitionVenue = (venueId) => {
    setForm((current) => ({
      ...current,
      venueIds: current.venueIds.filter((id) => id !== String(venueId)),
    }));
  };

  const updateDocument = (index, key, value) => {
    setForm((current) => ({
      ...current,
      documents: current.documents.map((document, documentIndex) =>
        documentIndex === index ? { ...document, [key]: value } : document
      ),
    }));
  };


  const addDocument = () => setForm((current) => ({ ...current, documents: [...current.documents, createDocument()] }));

  const removeDocument = (index) => {
    setForm((current) => ({
      ...current,
      documents: current.documents.length > 1 ? current.documents.filter((_, itemIndex) => itemIndex !== index) : [createDocument()],
    }));
  };


  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSportInformation({
        idevent,
        idsport,
        payload: {
          competition: form.competition,
          venues: form.venueIds.map((id) => ({ id: Number(id) })),
          technicalMeeting: {
            date: form.technicalMeeting.date || null,
            time: form.technicalMeeting.time || null,
            venueId: form.technicalMeeting.venueId ? Number(form.technicalMeeting.venueId) : null,
            locationText: form.technicalMeeting.locationText || null,
          },
          documents: form.documents
            .map((document) => ({ title: document.title.trim(), link: document.link.trim() }))
            .filter((document) => document.title && document.link),
          trainings: orderTrainingsForSave(form.trainings)
            .map((training) => ({
              id: training.id || null,
              date: training.date || null,
              startTime: training.startTime || null,
              endTime: training.endTime || null,
              institutionId: training.institutionId ? Number(training.institutionId) : null,
              institutionName: training.institutionName || null,
              venueId: training.venueId ? Number(training.venueId) : null,
              notes: training.notes || null,
              sortOrder: training.sortOrder,
            }))
            .filter((training) => training.date || training.startTime || training.endTime || training.institutionId || training.institutionName || training.venueId || training.notes),
        },
      });

      toast.success("¡Información pública guardada correctamente!");
      await loadData();
    } catch (error) {
      toast.error("No se pudo guardar la información pública");
      console.error("Error saving sport information", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex justify-center bg-gray-100 p-4">
      <div className="w-full max-w-5xl rounded-lg bg-white p-8 shadow-lg">
        <div className="mb-6 flex items-start gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-white">
            <FaBookOpen />
          </span>
          <div>
            <h2 className="text-2xl font-semibold text-gray-800">Información Pública</h2>
            <p className="mt-1 text-sm text-gray-500">
              Estos datos alimentan el tab Información de Hayllis 2.0 para esta disciplina.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-center font-semibold text-blue-700">
            Cargando información...
          </div>
        ) : null}

        {loadWarning ? (
          <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-800">
            {loadWarning}
          </div>
        ) : null}

        <div className="grid gap-6">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="grid gap-2 text-sm font-semibold text-gray-700">
              Fecha inicio competencia
              <input
                type="date"
                value={form.competition.startDate}
                onChange={(event) => updateCompetition("startDate", event.target.value)}
                className="rounded-lg border border-gray-300 px-4 py-2 font-normal focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>
            <label className="grid gap-2 text-sm font-semibold text-gray-700">
              Fecha fin competencia
              <input
                type="date"
                value={form.competition.endDate}
                onChange={(event) => updateCompetition("endDate", event.target.value)}
                className="rounded-lg border border-gray-300 px-4 py-2 font-normal focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>
          </div>

          <div className="rounded-xl border border-gray-200 p-4">
            <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-700">Sedes de competencia</h3>
            {venueOptions.length ? (
              <div className="grid gap-4">
                <div className="grid gap-3 md:grid-cols-[1fr,auto] md:items-end">
                  <SearchableVenueSelect
                    label="Buscar sede"
                    value={selectedCompetitionVenueId}
                    onChange={setSelectedCompetitionVenueId}
                    options={availableCompetitionVenues}
                    placeholder="Escribe, por ejemplo: viden"
                  />
                  <button
                    type="button"
                    onClick={addCompetitionVenue}
                    disabled={!selectedCompetitionVenueId}
                    className="inline-flex items-center justify-center gap-2 rounded bg-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FaPlus /> Agregar sede
                  </button>
                </div>

                {selectedCompetitionVenues.length ? (
                  <div className="grid gap-2">
                    {selectedCompetitionVenues.map((venue) => (
                      <div key={venue.id} className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-gray-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <span className="block font-semibold text-gray-800">{venue.name}</span>
                          {venue.address ? <span className="text-xs text-gray-500">{venue.address}</span> : null}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeCompetitionVenue(venue.id)}
                          className="inline-flex w-fit items-center gap-2 rounded bg-red-100 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-200"
                        >
                          <FaTrash /> Quitar
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-500">
                    Todavía no has agregado sedes de competencia para esta disciplina.
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-700">
                No se encontraron sedes activas para el departamento del evento. Puedes guardar reunión técnica con texto manual y volver a intentar cuando existan sedes disponibles.
              </div>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 p-4">
            <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-700">Reunión técnica</h3>
            <div className="grid gap-x-6 gap-y-4 md:grid-cols-2">
              <label className="grid gap-2 text-sm font-semibold text-gray-700">
                Fecha
                <input
                  type="date"
                  value={form.technicalMeeting.date}
                  onChange={(event) => updateTechnicalMeeting("date", event.target.value)}
                  className="rounded-lg border border-gray-300 px-4 py-2 font-normal focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </label>
              <label className="grid gap-2 text-sm font-semibold text-gray-700">
                Hora
                <input
                  type="text"
                  placeholder="5pm"
                  value={form.technicalMeeting.time}
                  onChange={(event) => updateTechnicalMeeting("time", event.target.value)}
                  className="rounded-lg border border-gray-300 px-4 py-2 font-normal focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </label>
              <SearchableVenueSelect
                label="Sede select"
                value={form.technicalMeeting.venueId}
                onChange={(value) => updateTechnicalMeeting("venueId", value)}
                options={venueOptions}
                placeholder="Buscar sede de reunión técnica"
              />
              <label className="grid gap-2 text-sm font-semibold text-gray-700">
                Texto manual de sede
                <input
                  type="text"
                  placeholder="Auditorio 1, FEDUP"
                  value={form.technicalMeeting.locationText}
                  onChange={(event) => updateTechnicalMeeting("locationText", event.target.value)}
                  className="rounded-lg border border-gray-300 px-4 py-2 font-normal focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </label>
            </div>
            <p className="mt-3 text-xs text-gray-500">
              Si eliges sede y también escribes texto manual, Hayllis mostrará primero la sede seleccionada; el texto manual queda como respaldo.
            </p>
          </div>

          <div className="rounded-xl border border-gray-200 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700">Documentos</h3>
              <button type="button" onClick={addDocument} className="inline-flex items-center gap-2 rounded bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300">
                <FaPlus /> Agregar documento
              </button>
            </div>
            <div className="grid gap-3">
              {form.documents.map((document, index) => (
                <div key={index} className="grid gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3 md:grid-cols-[1fr,1.4fr,auto]">
                  <input
                    type="text"
                    placeholder="Título"
                    value={document.title}
                    onChange={(event) => updateDocument(index, "title", event.target.value)}
                    className="rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                  <input
                    type="text"
                    placeholder="Link"
                    value={document.link}
                    onChange={(event) => updateDocument(index, "link", event.target.value)}
                    className="rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                  <button type="button" onClick={() => removeDocument(index)} className="flex h-10 w-10 items-center justify-center rounded bg-red-100 text-red-700 hover:bg-red-200" aria-label="Eliminar documento">
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <TrainingEditor
            trainings={form.trainings}
            onChange={(trainings) =>
              setForm((current) => ({ ...current, trainings }))
            }
            institutionOptions={institutionOptions}
            venueOptions={venueOptions}
          />

          <div className="flex justify-end border-t border-gray-100 pt-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || saving}
              className="inline-flex w-full items-center justify-center gap-2 rounded bg-blue-500 px-5 py-3 font-semibold text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              <FaSave />
              {saving ? "Guardando..." : "Guardar información"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardConfigSportInformation;
